import { Star, Trash2 } from "lucide-react";
import * as React from "react";

import { type UIPresetOut, overridesApi } from "@/api/overrides";
import { EmptyData } from "@/components/illustrations/EmptyData";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { useUIStore } from "@/stores/ui";

/**
 * TablePresets — list user's saved table presets (Doc 23 §13.4).
 *
 * Add/edit preset xảy ra inline khi user dùng table widget trong chat —
 * page này chỉ list + xoá + set default.
 */
export function TablePresets(): JSX.Element {
  const [presets, setPresets] = React.useState<UIPresetOut[]>([]);
  const [busy, setBusy] = React.useState(false);
  const pushToast = useUIStore((s) => s.pushToast);

  const refresh = React.useCallback(async () => {
    try {
      const list = await overridesApi.listPresets();
      setPresets(list);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải presets",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const setDefault = async (p: UIPresetOut): Promise<void> => {
    setBusy(true);
    try {
      await overridesApi.upsertPreset({
        preset_type: p.preset_type,
        preset_key: p.preset_key,
        config: p.config,
        is_default: true,
      });
      pushToast({ variant: "success", title: `Set default ${p.preset_key}` });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p: UIPresetOut): Promise<void> => {
    if (!confirm(`Xoá preset ${p.preset_key}?`)) return;
    setBusy(true);
    try {
      await overridesApi.deletePreset(p.preset_type, p.preset_key);
      pushToast({ variant: "success", title: "Đã xoá" });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  // Group by preset_type
  const grouped: Record<string, UIPresetOut[]> = {};
  for (const p of presets) {
    (grouped[p.preset_type] ??= []).push(p);
  }

  return (
    <div className="max-w-3xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold">UI Presets cá nhân</h1>
        <p className="text-sm text-muted-foreground">
          Cấu hình lưu lại cho table / chart / kpi widget. Tạo preset trong
          chat khi AI render widget.
        </p>
      </header>

      {presets.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              hero={<EmptyData size={120} />}
              title="Chưa có preset nào"
              description="Preset lưu cấu hình hiển thị widget (table/chart/kpi) cho riêng bạn."
              hint='💡 Trong chat: render table → ⚙ → "Lưu preset" để bắt đầu.'
            />
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([type, list]) => (
          <Card key={type}>
            <CardHeader>
              <CardTitle className="text-sm">{type}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {list.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 rounded border bg-muted/30 p-2"
                >
                  <code className="text-xs font-medium">{p.preset_key}</code>
                  {p.is_default ? (
                    <Badge variant="default" className="text-[10px]">
                      <Star className="size-3 mr-0.5" /> Default
                    </Badge>
                  ) : null}
                  <span className="text-[11px] text-muted-foreground">
                    {new Date(p.updated_at).toLocaleString("vi-VN")}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    {!p.is_default ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void setDefault(p)}
                        disabled={busy}
                      >
                        <Star className="size-3.5 mr-1" /> Default
                      </Button>
                    ) : null}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      onClick={() => void remove(p)}
                      disabled={busy}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
